/* eslint-disable import/no-default-export */

interface Config {
  API_URL: string;
  /**
   * Deployment environment label (`local` | `preprod` | `prd`) — attached to
   * every analytics event as the `environment` global property (task 426).
   * Falls back to `unknown` rather than a guessed value so a misconfigured
   * deploy is visible in the analytics stream instead of masquerading as local.
   */
  ENVIRONMENT: string;
  OPENPANEL_CLIENT_ID: string;
  OPENPANEL_API_URL: string;
  /**
   * Documentation website base URL (task 538). No fallback: a missing
   * `VITE_DOCS_URL` must break the Help menu's Documentation link visibly
   * rather than silently ship a guessed host.
   */
  DOCS_URL: string;
  /**
   * Function URL of the support-ticket intake Lambda (task 606). No fallback,
   * per repo convention — and, unlike the other URL reads, an empty value is a
   * legitimate state rather than a misconfiguration: the Lambda only exists in
   * preprod and prd, so local development leaves it empty and the Support page
   * degrades to a mailto link instead of rendering a form that cannot post.
   */
  SUPPORT_LAMBDA_URL: string;
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT_ID: string;
  UNLEASH_URL: string;
  UNLEASH_CLIENT_KEY: string;
  UNLEASH_APP_NAME: string;
  FEATURE_FLAGS_BYPASS: boolean;
}

const config: Config = {
  API_URL: import.meta.env.VITE_API_URL as string,
  ENVIRONMENT: (import.meta.env.VITE_ENVIRONMENT as string) || 'unknown',
  // Empty under Vitest (MODE=test) even when the container sets the variable:
  // an empty client id is what disables the SDK, and the component suite must
  // never emit analytics — it renders the providers that identify and track.
  OPENPANEL_CLIENT_ID:
    import.meta.env.MODE === 'test'
      ? ''
      : import.meta.env.VITE_OPENPANEL_CLIENT_ID || '',
  OPENPANEL_API_URL: import.meta.env.VITE_OPENPANEL_API_URL || '',
  DOCS_URL: import.meta.env.VITE_DOCS_URL as string,
  SUPPORT_LAMBDA_URL: import.meta.env.VITE_SUPPORT_LAMBDA_URL as string,
  KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL as string,
  KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM as string,
  KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string,
  UNLEASH_URL: import.meta.env.VITE_UNLEASH_URL as string,
  UNLEASH_CLIENT_KEY: import.meta.env.VITE_UNLEASH_CLIENT_KEY as string,
  UNLEASH_APP_NAME: import.meta.env.VITE_UNLEASH_APP_NAME as string,
  FEATURE_FLAGS_BYPASS: import.meta.env.VITE_FEATURE_FLAGS_BYPASS === 'true',
};

/**
 * The monitored support address behind the Help menu's "Contact support"
 * entry (task 538). Not environment-specific, so it is a constant here rather
 * than an env read — but it lives in config so no component hardcodes it.
 *
 * Same address the documentation website's footer uses. Pending kv's explicit
 * confirmation that this is the monitored inbox.
 */
export const SUPPORT_EMAIL = 'support@monarchworks.ai';

/**
 * Live example demos (task 540).
 *
 * Three working applications deployed on raideil.com — a domain deliberately
 * unaffiliated with MonarchWorks — each consuming real Innovate endpoints the
 * way a customer's own application would. Each proves exactly one consumption
 * pattern, so the product can show the demo that matches what the User is
 * configuring.
 *
 * Fixed public URLs rather than env reads: they are the same in every
 * environment. They live here so no surface hardcodes a demo inline.
 *
 * The running demos are public; the innovate-examples source repository is
 * private and stays private until the go-public backlog task
 * (`project/tasks/backlog/496-innovate-examples-repo-private-to-public.md`)
 * executes — see the `sourceUrl` TODO below.
 */
export type LiveExampleKey = 'widget' | 'shop' | 'hostedUi';

export interface LiveExample {
  key: LiveExampleKey;
  /** Demo name as the User sees it. */
  name: string;
  /** One line: the consumption pattern this demo proves. */
  pattern: string;
  url: string;
  // TODO (backlog task 496): add `sourceUrl` here, and render a "view the
  // source" link beside each demo, once innovate-examples is public.
}

export const LIVE_EXAMPLES: Record<LiveExampleKey, LiveExample> = {
  widget: {
    key: 'widget',
    name: 'Support chat widget',
    pattern:
      'A marketing site calling an AI Service straight from the browser — public mode with an origins allow-list, no credential in the page.',
    url: 'https://www.raideil.com',
  },
  shop: {
    key: 'shop',
    name: 'Online shop back office',
    pattern:
      "An application that signs its own users in and calls the AI Service server-side with an account API key the browser never sees.",
    url: 'https://shop.raideil.com',
  },
  hostedUi: {
    key: 'hostedUi',
    name: 'Hosted lead form',
    pattern:
      'A generated interface copied out of Innovate and hosted on the team’s own site, calling the same AI Service unchanged.',
    url: 'https://leads.raideil.com',
  },
};

/**
 * Knowledge document upload envelope (task 574).
 *
 * These mirror the server-side envelope resolved by kv in the 2026-08-17
 * console review and enforced by innovate-service
 * (`KNOWLEDGE_ALLOWED_MIME_TYPES` / `KNOWLEDGE_MAX_DOCUMENT_BYTES`, task 571):
 * docx, pdf, xlsx, txt, md and csv, at 10 MB per document. The service remains
 * the authority — the UI states the limits up front and rejects an obviously
 * out-of-envelope file before spending an upload, so a User never discovers the
 * rule from a 400.
 *
 * Not env reads: the envelope is the same in every environment, so it lives
 * here as a constant rather than being hardcoded in the upload component.
 */
export const KNOWLEDGE_UPLOAD_EXTENSIONS = [
  'docx',
  'pdf',
  'xlsx',
  'txt',
  'md',
  'csv',
] as const;

/**
 * The envelope as MIME types (task 1084), for the one moment a file's NAME is
 * not readable: while it is being dragged over the Documents page the browser
 * exposes only each item's type. An empty type is common (`.md` on most systems)
 * and is not a rejection — the extension check on drop is the real gate.
 */
export const KNOWLEDGE_UPLOAD_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'text/csv',
  'application/vnd.ms-excel',
] as const;

/** `accept` attribute for the document file input, derived from the envelope. */
export const KNOWLEDGE_UPLOAD_ACCEPT = KNOWLEDGE_UPLOAD_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(',');

/** Human-readable format list, e.g. "DOCX, PDF, XLSX, TXT, MD, CSV". */
export const KNOWLEDGE_UPLOAD_FORMATS_LABEL = KNOWLEDGE_UPLOAD_EXTENSIONS.map(
  (extension) => extension.toUpperCase()
).join(', ');

/** Hard per-document size cap, in bytes. */
export const KNOWLEDGE_MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** The same cap as the UI states it. */
export const KNOWLEDGE_MAX_DOCUMENT_LABEL = '10 MB';

/**
 * How often the Documents surface re-reads a document that has not settled.
 * Ingestion is a background worker job, so `pending`/`indexing` rows are
 * polled; `ready`/`failed` rows stop the timer (see `src/hooks/useKnowledge.ts`).
 */
export const KNOWLEDGE_STATUS_POLL_MS = 5000;

/**
 * How often the Custom Domains section re-reads a domain that has not settled
 * (task 850). Verification is a background worker watching DNS and the managed
 * certificate, so `pending_dns`/`verifying` rows are polled; `active`/`failed`
 * rows stop the timer, and so does unmounting the section (see
 * `src/hooks/useCustomDomains.ts`).
 *
 * Ten seconds rather than the knowledge library's five: a customer editing a
 * DNS record is on a minutes-long clock, not a seconds-long one, so a slower
 * poll costs nothing perceptible and halves the idle traffic.
 */
export const CUSTOM_DOMAIN_STATUS_POLL_MS = 10000;

export default config;
